import React, { createContext, useState, useContext, useEffect } from 'react';
import { AUTH_BASE_URL } from '../config/api';
import { useAuth } from './AuthContext';

const SessionContext = createContext();

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export const SessionProvider = ({ children }) => {
  const { user } = useAuth();
  const [sessionData, setSessionData] = useState({
    user: null,
    teams: [],
    permissions: [],
    credits: null,
    loading: true,
    error: null
  });

  const fetchSession = async () => {
    if (!user) {
      setSessionData({
        user: null,
        teams: [],
        permissions: [],
        credits: null,
        loading: false,
        error: null
      });
      return;
    }

    try {
      setSessionData(prev => ({ ...prev, loading: true, error: null }));
      
      const response = await fetch(`${AUTH_BASE_URL}/api/auth/session`, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch session data');
      }
      
      const data = await response.json();
      
      setSessionData({
        user: data.user,
        teams: data.teams || [],
        permissions: data.permissions || [],
        credits: data.credits,
        loading: false,
        error: null
      });
    } catch (error) {
      console.error('Session fetch failed:', error);
      setSessionData(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    }
  };

  useEffect(() => {
    fetchSession();
  }, [user]);

  const value = {
    ...sessionData,
    refreshSession: fetchSession,
    isAdmin: sessionData.user?.isAdmin || false,
    isSuperAdmin: sessionData.user?.isSuperAdmin || false,
    hasPermission: (permission) => {
      return sessionData.user?.isAdmin || sessionData.permissions.includes(permission);
    }
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};